import { F32 } from '../engine/typed-arrays.js';
import { TU, SS } from '../engine/gpu-flags.js';
import { createUniformBuffer } from '../resource/gpu-buffers.js';
import { getBilinearSampler } from '../resource/samplers.js';
import { createRenderTask } from '../frame-graph/render-task.js';
import { createShadowParamsUBO, createSharedShadowUBO, createShadowCamera, createShadowRenderTarget, casterVersionSum, computeDirectionalLightMatrix, writeShadowUboFields, updateShadowCameraBase } from './shadow-base.js';
import { packMat4IntoF32 } from '../math/pack-mat4-into-f32.js';

const blurVertSrc = "struct c{@builtin(position) clipPos:vec4<f32>,@location(0) sampleCenter:vec2<f32>}@vertex fn main(@builtin(vertex_index) d:u32)->c{var e=array<vec2<f32>,3>(vec2<f32>(-1.0,-1.0),vec2<f32>(3.0,-1.0),vec2<f32>(-1.0,3.0));var a:c;let b=e[d];a.clipPos=vec4<f32>(b,0.0,1.0);a.sampleCenter=b*vec2<f32>(0.5,-0.5)+0.5;return a;}";

let esmShadowTaskResources = null;
let createStandardEsmShadowMaterialView;
let createPbrEsmShadowMaterialView;
let createNodeEsmShadowMaterialView;
function getEsmShadowTaskResourceMap() {
  esmShadowTaskResources ??= /* @__PURE__ */ new WeakMap();
  return esmShadowTaskResources;
}
function setEsmShadowTaskResources(sg, resources) {
  getEsmShadowTaskResourceMap().set(sg, resources);
}
function getEsmShadowTaskResources(sg) {
  return esmShadowTaskResources?.get(sg) ?? null;
}
async function preloadEsmShadowTaskState(casterMeshes) {
  const loads = [];
  let needsStandard = false;
  let needsPbr = false;
  let needsNode = false;
  for (const mesh of casterMeshes) {
    const family = mesh.material?._buildGroup._materialFamily;
    needsStandard ||= family === "standard";
    needsPbr ||= family === "pbr";
    needsNode ||= family === "node";
  }
  if (needsStandard && !createStandardEsmShadowMaterialView) {
    loads.push(
      import('../material/standard/esm-shadow-view.js').then((module) => {
        createStandardEsmShadowMaterialView = module.createStandardEsmShadowMaterialView;
      })
    );
  }
  if (needsPbr && !createPbrEsmShadowMaterialView) {
    loads.push(
      import('../material/pbr/esm-shadow-view.js').then((module) => {
        createPbrEsmShadowMaterialView = module.createPbrEsmShadowMaterialView;
      })
    );
  }
  if (needsNode && !createNodeEsmShadowMaterialView) {
    loads.push(
      import('../material/node/esm-shadow-view.js').then((module) => {
        createNodeEsmShadowMaterialView = module.createNodeEsmShadowMaterialView;
      })
    );
  }
  await Promise.all(loads);
}
function nearestBestKernel(idealKernel) {
  const v = Math.round(Math.max(idealKernel, 1));
  for (const k of [v, v - 1, v + 1, v - 2, v + 2]) {
    if (k % 2 !== 0 && Math.floor(k / 2) % 2 === 0 && k > 0) {
      return Math.max(k, 3);
    }
  }
  return Math.max(v, 3);
}
function gaussianWeight(x) {
  const sigma = 1 / 3;
  return Math.exp(-(x * x / (2 * sigma * sigma))) / (Math.sqrt(2 * Math.PI) * sigma);
}
function createKernelBlurSamples(idealKernel) {
  const n = nearestBestKernel(idealKernel);
  const centerIndex = (n - 1) / 2;
  const offsets = [];
  const weights = [];
  let totalWeight = 0;
  for (let i = 0; i < n; i++) {
    const u = i / (n - 1);
    const weight = gaussianWeight(u * 2 - 1);
    offsets[i] = i - centerIndex;
    weights[i] = weight;
    totalWeight += weight;
  }
  for (let i = 0; i < weights.length; i++) {
    weights[i] = weights[i] / totalWeight;
  }
  const linearOffsets = [];
  const linearWeights = [];
  for (let i = 0; i <= centerIndex; i += 2) {
    const j = Math.min(i + 1, Math.floor(centerIndex));
    if (i === j) {
      linearOffsets.push(offsets[i]);
      linearWeights.push(weights[i]);
      continue;
    }
    const sharedCell = j === centerIndex;
    const weightLinear = weights[i] + weights[j] * (sharedCell ? 0.5 : 1);
    const offsetLinear = offsets[i] + 1 / (1 + weights[i] / weights[j]);
    if (offsetLinear === 0) {
      linearOffsets.push(offsets[i], offsets[i + 1]);
      linearWeights.push(weights[i], weights[i + 1]);
    } else {
      linearOffsets.push(offsetLinear, -offsetLinear);
      linearWeights.push(weightLinear, weightLinear);
    }
  }
  return { offsets: linearOffsets, weights: linearWeights };
}
function wgslFloat(value) {
  const n = Object.is(value, -0) ? 0 : value;
  let s = n.toPrecision(10);
  if (!/[.eE]/.test(s)) {
    s += ".0";
  }
  return s;
}
function createShadowBlurFragmentWGSL(blurKernel) {
  const { offsets, weights } = createKernelBlurSamples(blurKernel);
  const count = offsets.length;
  return `struct BlurParams{delta:vec2<f32>,_pad:vec2<f32>,};@group(0)@binding(0)var<uniform>params:BlurParams;@group(0)@binding(1)var srcTex:texture_2d<f32>;@group(0)@binding(2)var srcSampler:sampler;const OFFSETS=array<f32,${count}>(${offsets.map(wgslFloat).join(",")});const WEIGHTS=array<f32,${count}>(${weights.map(wgslFloat).join(",")});@fragment fn main(@location(0)sampleCenter:vec2<f32>)->@location(0)vec4<f32>{var blend=vec4<f32>(0.0);for(var i=0u;i<${count}u;i=i+1u){blend+=textureSample(srcTex,srcSampler,sampleCenter+params.delta*OFFSETS[i])*WEIGHTS[i];}return blend;}`;
}
function ensureEsmShadowTaskState(engine, scene, sg, casterMeshes, existingState) {
  const existing = existingState;
  if (existing) {
    if (existing._casterMeshes === casterMeshes) {
      return existing;
    }
    existing._task.dispose();
  }
  const resources = getEsmShadowTaskResources(sg);
  if (!resources) {
    throw new Error("ShadowTask: missing ESM metadata.");
  }
  const materialViews = /* @__PURE__ */ new Map();
  const camera = createShadowCamera(sg);
  const taskState = {
    _task: createRenderTask(
      {
        name: "esm",
        rt: createShadowRenderTarget(sg, resources._esmTexture, resources._depthBuffer),
        clr: true,
        clrColor: { r: 0, g: 0, b: 0, a: 0 },
        cam: camera,
        _skipClusteredLights: true
      },
      engine,
      scene
    ),
    _camera: camera,
    _cameraVersion: 0,
    _lastCasterVersion: -1,
    _lastLightVersion: -1,
    _lastFoVersion: -1,
    _casterMeshes: casterMeshes,
    _scene: scene
  };
  for (const mesh of casterMeshes) {
    const material = mesh.material;
    if (material) {
      taskState._task.addMesh(mesh, { material: getEsmShadowView(material, materialViews, sg._shadowParamsUBO) });
    }
  }
  return taskState;
}
function renderEsmShadowMap(engine, sg, state) {
  const resources = getEsmShadowTaskResources(sg);
  if (!resources) {
    return 0;
  }
  const casterMeshes = state._casterMeshes;
  const casterVersion = casterVersionSum(casterMeshes);
  const lightVersion = sg._light.worldMatrixVersion;
  const foCam = engine.useFloatingOrigin ? state._scene.camera : null;
  const foVersion = foCam ? foCam.worldMatrixVersion : 0;
  const offX = foCam ? foCam.worldMatrix[12] : 0;
  const offY = foCam ? foCam.worldMatrix[13] : 0;
  const offZ = foCam ? foCam.worldMatrix[14] : 0;
  if (!sg._config._forceRefreshEveryFrame && casterVersion === state._lastCasterVersion && lightVersion === state._lastLightVersion && foVersion === state._lastFoVersion) {
    return 0;
  }
  const matrix = computeDirectionalLightMatrix(sg._light, casterMeshes, sg._config._orthoMinZ, sg._config._orthoMaxZ, offX, offY, offZ);
  if (shadowMatrixChanged(sg._lightMatrix, matrix._viewProj)) {
    packMat4IntoF32(sg._lightMatrix, matrix._viewProj, 0);
    sg._version++;
    writeShadowUboFields(resources._shadowUboData, sg);
    engine._device.queue.writeBuffer(sg._shadowUBO, 0, resources._shadowUboData);
  }
  updateShadowCamera(state, matrix);
  state._lastCasterVersion = casterVersion;
  state._lastLightVersion = lightVersion;
  state._lastFoVersion = foVersion;
  let draws = state._task.execute?.() ?? 0;
  const encoder = engine._currentEncoder;
  const bh = encoder.beginRenderPass({
    colorAttachments: [
      {
        view: resources._blurTexH.createView(),
        loadOp: "clear",
        storeOp: "store",
        clearValue: { r: 0, g: 0, b: 0, a: 0 }
      }
    ]
  });
  bh.setPipeline(resources._blurPipeline);
  bh.setBindGroup(0, resources._blurHBG);
  bh.draw(3);
  bh.end();
  const bv = encoder.beginRenderPass({
    colorAttachments: [
      {
        view: sg._depthTexture.createView(),
        loadOp: "clear",
        storeOp: "store",
        clearValue: { r: 0, g: 0, b: 0, a: 0 }
      }
    ]
  });
  bv.setPipeline(resources._blurPipeline);
  bv.setBindGroup(0, resources._blurVBG);
  bv.draw(3);
  bv.end();
  draws += 2;
  return draws;
}
function updateShadowCamera(state, matrix) {
  state._cameraVersion++;
  updateShadowCameraBase(state._camera, state._cameraVersion, matrix._near, matrix._far, matrix._view, matrix._viewProj);
}
function getEsmShadowView(material, cache, shadowParamsUBO) {
  const cached = cache.get(material);
  if (cached) {
    return cached;
  }
  const family = material._buildGroup._materialFamily;
  let view;
  if (family === "standard") {
    view = createStandardEsmShadowMaterialView(material, shadowParamsUBO);
  } else if (family === "pbr") {
    view = createPbrEsmShadowMaterialView(material, shadowParamsUBO);
  } else if (family === "node") {
    view = createNodeEsmShadowMaterialView(material, shadowParamsUBO);
  }
  cache.set(material, view);
  return view;
}
function shadowMatrixChanged(a, b) {
  for (let i = 0; i < 16; i++) {
    if (a[i] !== b[i]) {
      return true;
    }
  }
  return false;
}
function createEsmDirectionalShadowGenerator(engine, _light, cfg = {}) {
  const device = engine._device;
  const mapSize = cfg.mapSize ?? 1024;
  const depthScale = cfg.depthScale ?? 50;
  const bias = cfg.bias ?? 5e-5;
  const blurKernel = cfg.blurKernel ?? 1;
  const blurScale = cfg.blurScale ?? 2;
  const darkness = cfg.darkness ?? 0;
  const frustumEdgeFalloff = cfg.frustumEdgeFalloff ?? 0;
  const orthoMinZ = cfg.orthoMinZ ?? 1;
  const orthoMaxZ = cfg.orthoMaxZ ?? 1e4;
  const forceRefreshEveryFrame = cfg.forceRefreshEveryFrame ?? false;
  const blurSize = mapSize / blurScale;
  const _config = {
    _mapSize: mapSize,
    _bias: bias,
    _orthoMinZ: orthoMinZ,
    _orthoMaxZ: orthoMaxZ,
    _forceRefreshEveryFrame: forceRefreshEveryFrame
  };
  const _shadowParamsUBO = createShadowParamsUBO(engine, bias, depthScale);
  const esmTexture = device.createTexture({
    size: { width: mapSize, height: mapSize },
    format: "rgba16float",
    usage: TU.RENDER_ATTACHMENT | TU.TEXTURE_BINDING
  });
  const depthBuf = device.createTexture({
    size: { width: mapSize, height: mapSize },
    format: "depth32float",
    usage: TU.RENDER_ATTACHMENT
  });
  const blurTexH = device.createTexture({
    size: { width: blurSize, height: blurSize },
    format: "rgba16float",
    usage: TU.RENDER_ATTACHMENT | TU.TEXTURE_BINDING
  });
  const blurTexV = device.createTexture({
    size: { width: blurSize, height: blurSize },
    format: "rgba16float",
    usage: TU.RENDER_ATTACHMENT | TU.TEXTURE_BINDING
  });
  const blurVert = device.createShaderModule({ code: blurVertSrc });
  const blurFrag = device.createShaderModule({ code: createShadowBlurFragmentWGSL(blurKernel) });
  const blurBGL = device.createBindGroupLayout({
    entries: [
      { binding: 0, visibility: SS.VERTEX | SS.FRAGMENT, buffer: { type: "uniform" } },
      { binding: 1, visibility: SS.FRAGMENT, texture: { sampleType: "float" } },
      { binding: 2, visibility: SS.FRAGMENT, sampler: { type: "filtering" } }
    ]
  });
  const blurPipeline = device.createRenderPipeline({
    layout: device.createPipelineLayout({ bindGroupLayouts: [blurBGL] }),
    vertex: { module: blurVert, entryPoint: "main" },
    fragment: { module: blurFrag, entryPoint: "main", targets: [{ format: "rgba16float" }] },
    primitive: { topology: "triangle-list", cullMode: "none" }
  });
  const blurSampler = getBilinearSampler(engine);
  const blurHData = new F32([1 / blurSize, 0, 0, 0]);
  const blurHUBO = createUniformBuffer(engine, blurHData);
  const blurHBG = device.createBindGroup({
    layout: blurBGL,
    entries: [
      { binding: 0, resource: { buffer: blurHUBO } },
      { binding: 1, resource: esmTexture.createView() },
      { binding: 2, resource: blurSampler }
    ]
  });
  const blurVData = new F32([0, 1 / blurSize, 0, 0]);
  const blurVUBO = createUniformBuffer(engine, blurVData);
  const blurVBG = device.createBindGroup({
    layout: blurBGL,
    entries: [
      { binding: 0, resource: { buffer: blurVUBO } },
      { binding: 1, resource: blurTexH.createView() },
      { binding: 2, resource: blurSampler }
    ]
  });
  const _lightMatrix = new F32(16);
  const _shadowsInfo = new F32([darkness, 0, depthScale, frustumEdgeFalloff]);
  const _depthValues = new F32([0, 1]);
  const { ubo: _shadowUBO, data: shadowUboData } = createSharedShadowUBO(engine, _lightMatrix, _depthValues, _shadowsInfo);
  const _depthTexture = blurTexV;
  const _depthSampler = blurSampler;
  const sg = {
    _shadowType: "esm",
    _light,
    _depthTexture,
    _depthSampler,
    _lightMatrix,
    _shadowsInfo,
    _depthValues,
    _shadowParamsUBO,
    _shadowUBO,
    _config,
    _version: 0
  };
  sg._preloadShadowTask = preloadEsmShadowTaskState;
  sg._ensureShadowTaskState = (engine2, scene, casterMeshes) => {
    const state = ensureEsmShadowTaskState(engine2, scene, sg, casterMeshes, sg._shadowTaskState ?? null);
    sg._shadowTaskState = state;
    return state;
  };
  sg._renderShadowMap = (engine2, state) => {
    return renderEsmShadowMap(engine2, sg, state);
  };
  setEsmShadowTaskResources(sg, {
    _esmTexture: esmTexture,
    _depthBuffer: depthBuf,
    _blurTexH: blurTexH,
    _blurPipeline: blurPipeline,
    _blurHBG: blurHBG,
    _blurVBG: blurVBG,
    _shadowUboData: shadowUboData,
    _blurKernel: blurKernel,
    _blurScale: blurScale
  });
  return sg;
}

export { createEsmDirectionalShadowGenerator, getEsmShadowTaskResources, setEsmShadowTaskResources };
//# sourceMappingURL=esm-directional-shadow-generator.js.map
